import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  RecoveryInfo,
  RecoveryManagerService
} from './services/recovery-manager.service';

@Component({
  selector: 'app-recovery-screen',
  templateUrl: './recovery-screen.component.html',
  styleUrls: ['./recovery-screen.component.scss']
})
export class RecoveryScreenComponent implements OnInit, OnDestroy {
  showCancelButton = false;

  recoveryInfo: RecoveryInfo | null = null;
  progress: number = 0;
  lastRecovery: RecoveryInfo | null = null;
  private subscription: Subscription = new Subscription();

  // Diagnóstico
  diagnosticMessages: string[] = [];
  recoveryInProgress = false;

  // Mensagens personalizadas baseadas no motivo da recuperação
  reasonMessages: { [key: string]: string } = {
    cache: 'Problemas com o cache do navegador foram detectados',
    storage: 'Problemas com o armazenamento local foram encontrados',
    localStorage:
      'Dados essenciais do aplicativo estão ausentes ou corrompidos',
    chaves: 'Configurações importantes do aplicativo precisam ser restauradas',
    network: 'Detectamos problemas de rede',
    performance: 'A aplicação estava apresentando lentidão',
    error: 'Erros inesperados foram detectados',
    timeout: 'Algumas operações estavam demorando muito tempo',
    default: 'Detectamos uma possível quebra do sistema no seu navegador'
  };

  constructor(
    private recoveryManager: RecoveryManagerService,
    private router: Router
  ) {
    this.addDiagnostic('Componente de recuperação inicializado');
  }

  ngOnInit(): void {
    this.addDiagnostic('ngOnInit executado');
    // Verificar status da recuperação
    this.subscription.add(
      this.recoveryManager.recoveryInProgress$.subscribe(inProgress => {
        this.recoveryInProgress = inProgress;
        this.addDiagnostic(
          `Estado de recuperação: ${inProgress ? 'ATIVO' : 'INATIVO'}`
        );

        if (!inProgress) {
          this.addDiagnostic(
            'Recuperação NÃO está em progresso, redirecionando...'
          );
          setTimeout(() => {
            this.router.navigate(['/'], {
              queryParams: { noRecovery: 'true' },
              replaceUrl: true
            });
          }, 1000);
        }
      })
    );

    // Receber informações de recuperação
    this.subscription.add(
      this.recoveryManager.recoveryInfo$.subscribe(info => {
        this.recoveryInfo = info;
        this.addDiagnostic(
          `Informações de recuperação recebidas: ${info ? 'SIM' : 'NÃO'}`
        );

        if (info) {
          this.addDiagnostic(`Motivo: ${info.reason}`);
          this.progress = info.progress || 0;
          this.simulateProgress();
        }
      })
    );

    // Obter última recuperação para histórico
    this.lastRecovery = this.recoveryManager.getLastRecovery();

    // Mostrar botão de cancelar após 15 segundos
    setTimeout(() => {
      this.showCancelButton = true;
    }, 15000);
  }

  ngOnDestroy(): void {
    this.addDiagnostic('Componente destruído');
    this.subscription.unsubscribe();
  }

  /**
   * Adiciona mensagem de diagnóstico
   */
  private addDiagnostic(message: string): void {
    const now = new Date().toISOString().substr(11, 8); // HH:MM:SS
    this.diagnosticMessages.push(`[${now}] ${message}`);
    console.log(`[RecoveryScreen] ${message}`);
  }

  /**
   * Força finalização da recuperação
   */
  forceFinish(): void {
    this.addDiagnostic('Usuário forçou finalização');
    this.recoveryManager.finishRecovery(true);
  }

  /**
   * Simula o progresso da recuperação para feedback visual
   */
  private simulateProgress(): void {
    if (!this.recoveryInfo) return;

    const interval = this.recoveryInfo.estimatedDuration * 10; // atualizações a cada 100ms
    const increment = 100 / interval;

    let counter = 0;
    const timer = setInterval(() => {
      counter++;

      // Incrementa o progresso, mas mantém abaixo de 95% para esperar finalização real
      if (this.progress < 95) {
        this.progress += increment;

        // Atualizar a etapa atual se aplicável
        if (this.recoveryInfo?.steps && this.recoveryInfo.steps.length > 0) {
          const newStep = Math.floor(
            (this.progress / 100) * this.recoveryInfo.steps.length
          );
          if (newStep !== this.recoveryInfo.currentStep) {
            this.recoveryManager.updateProgress(this.progress, newStep);
          }
        } else {
          this.recoveryManager.updateProgress(this.progress);
        }
      }

      // Se atingiu o tempo estimado e ainda está na tela, finalizar
      if (counter >= interval && this.progress >= 95) {
        clearInterval(timer);
        this.progress = 100;
        this.recoveryManager.updateProgress(100);

        // Dar um pequeno delay para mostrar 100% antes de redirecionar
        setTimeout(() => {
          this.recoveryManager.finishRecovery(true);
        }, 500);
      }
    }, 100);
  }

  /**
   * Obtém a mensagem apropriada baseada no motivo da recuperação
   */
  getReasonMessage(): string {
    if (!this.recoveryInfo) return this.reasonMessages['default'];

    const reason = this.recoveryInfo.reason.toLowerCase();

    if (reason.includes('chaves') || reason.includes('localStorage')) {
      return 'Detectamos um problema com os dados salvos da aplicação';
    }

    for (const key of Object.keys(this.reasonMessages)) {
      if (reason.includes(key)) {
        return this.reasonMessages[key];
      }
    }

    return this.reasonMessages['default'];
  }

  /**
   * Formata a última recuperação para exibição
   */
  getLastRecovery(): string {
    if (!this.lastRecovery) return '';

    const date = this.recoveryManager.formatDate(
      new Date(this.lastRecovery.startTime)
    );
    return `Última recuperação: ${date} - ${this.lastRecovery.reason}`;
  }

  /**
   * Obtém a etapa atual do processo de recuperação
   */
  getCurrentStep(): string {
    if (
      !this.recoveryInfo ||
      !this.recoveryInfo.steps ||
      this.recoveryInfo.currentStep === undefined
    ) {
      return '';
    }

    const step = this.recoveryInfo.currentStep;
    if (step < this.recoveryInfo.steps.length) {
      return this.recoveryInfo.steps[step];
    }

    return this.recoveryInfo.steps[this.recoveryInfo.steps.length - 1];
  }
}
